import React, { useState, useEffect, useCallback } from 'react';
import { expensesApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Wallet, Plus, Trash2, Calendar, Euro, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = [
  { v: 1, l: 'Gennaio' }, { v: 2, l: 'Febbraio' }, { v: 3, l: 'Marzo' },
  { v: 4, l: 'Aprile' }, { v: 5, l: 'Maggio' }, { v: 6, l: 'Giugno' },
  { v: 7, l: 'Luglio' }, { v: 8, l: 'Agosto' }, { v: 9, l: 'Settembre' },
  { v: 10, l: 'Ottobre' }, { v: 11, l: 'Novembre' }, { v: 12, l: 'Dicembre' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - i);

const todayISO = () => new Date().toISOString().slice(0, 10);

const ExpensesPage = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12; 0=Tutti
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form state (inline)
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayISO());
  const [creating, setCreating] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', amount: '', date: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = month === 0 ? { year } : { year, month };
      const r = await expensesApi.list(params.year, params.month);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Errore nel caricamento delle uscite');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      toast.error('Inserisci un nome per l\'uscita');
      return;
    }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt < 0) {
      toast.error('Importo non valido');
      return;
    }
    setCreating(true);
    try {
      await expensesApi.create({
        name: name.trim(),
        description: description.trim(),
        amount: amt,
        date: date || todayISO(),
      });
      toast.success('Uscita registrata');
      setName(''); setDescription(''); setAmount(''); setDate(todayISO());
      // Se la data creata non ricade nel filtro corrente, aggiorno il filtro per farla vedere
      const [yy, mm] = (date || todayISO()).split('-').map(Number);
      if (yy !== year || (month !== 0 && mm !== month)) {
        setYear(yy);
        setMonth(mm);
      } else {
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await expensesApi.delete(deleteId);
      toast.success('Uscita eliminata');
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      name: row.name || '',
      description: row.description || '',
      amount: String(row.amount ?? ''),
      date: row.date || todayISO(),
    });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error('Nome mancante'); return; }
    const amt = parseFloat(editForm.amount);
    if (isNaN(amt) || amt < 0) { toast.error('Importo non valido'); return; }
    setSavingEdit(true);
    try {
      await expensesApi.update(editingId, {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        amount: amt,
        date: editForm.date || todayISO(),
      });
      toast.success('Uscita aggiornata');
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento');
    } finally {
      setSavingEdit(false);
    }
  };

  const currentPeriodLabel =
    month === 0 ? `Anno ${year}` : `${MONTHS.find(m => m.v === month)?.l} ${year}`;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold font-['Manrope'] flex items-center gap-2">
            <Wallet className="w-6 h-6 text-rose-600" />
            Uscite
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Registra le spese aziendali. Il totale è calcolato in fondo alla lista.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
              Mese
            </Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger className="h-9 w-[140px]" data-testid="filter-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Tutti i mesi</SelectItem>
                {MONTHS.map(m => (
                  <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
              Anno
            </Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
              <SelectTrigger className="h-9 w-[100px]" data-testid="filter-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Add form */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="w-4 h-4 text-rose-600" />
            Nuova uscita
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label htmlFor="exp-name" className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
                Nome *
              </Label>
              <Input
                id="exp-name"
                data-testid="expense-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Es. Carburante furgone"
                autoComplete="off"
                required
              />
            </div>
            <div className="md:col-span-4">
              <Label htmlFor="exp-desc" className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
                Descrizione
              </Label>
              <Input
                id="exp-desc"
                data-testid="expense-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Note opzionali"
                autoComplete="off"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="exp-amt" className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
                Importo (€) *
              </Label>
              <Input
                id="exp-amt"
                data-testid="expense-amount-input"
                type="number"
                step="any"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="text-right font-['JetBrains_Mono']"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="exp-date" className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
                Data
              </Label>
              <Input
                id="exp-date"
                data-testid="expense-date-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="md:col-span-12 flex justify-end">
              <Button
                type="submit"
                data-testid="expense-add-btn"
                disabled={creating}
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {creating ? 'Salvo…' : 'Aggiungi uscita'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            Uscite — {currentPeriodLabel}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              ({items.length} {items.length === 1 ? 'voce' : 'voci'})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              <Wallet className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Nessuna uscita per {currentPeriodLabel.toLowerCase()}.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[110px] table-header-swiss">Data</TableHead>
                  <TableHead className="table-header-swiss">Nome</TableHead>
                  <TableHead className="table-header-swiss">Descrizione</TableHead>
                  <TableHead className="table-header-swiss text-right w-[140px]">Importo</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(it => (
                  <TableRow key={it.id} data-testid={`expense-row-${it.id}`} className="table-row-swiss">
                    <TableCell className="table-cell-swiss font-['JetBrains_Mono'] text-xs">
                      {it.date}
                    </TableCell>
                    <TableCell className="table-cell-swiss font-medium">{it.name}</TableCell>
                    <TableCell className="table-cell-swiss text-muted-foreground text-sm">
                      {it.description || '—'}
                    </TableCell>
                    <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono'] font-semibold text-rose-700">
                      € {Number(it.amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="table-cell-swiss text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          data-testid={`expense-edit-${it.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(it)}
                          className="h-8 w-8"
                          aria-label="Modifica uscita"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`expense-delete-${it.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(it.id)}
                          className="h-8 w-8 text-destructive hover:bg-destructive/10"
                          aria-label="Elimina uscita"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Total sticky-ish box at bottom */}
      <div
        data-testid="expenses-total-box"
        className="flex items-center justify-between rounded-md border-2 border-rose-600 bg-rose-50 px-5 py-4"
      >
        <div className="flex items-center gap-2 text-rose-900 font-semibold">
          <Euro className="w-5 h-5" />
          Totale {currentPeriodLabel}
        </div>
        <div
          data-testid="expenses-total-amount"
          className="text-2xl font-bold font-['JetBrains_Mono'] text-rose-700"
        >
          € {Number(total || 0).toFixed(2)}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editingId} onOpenChange={(o) => { if (!o) setEditingId(null); }}>
        <DialogContent data-testid="expense-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5" /> Modifica uscita</DialogTitle>
            <DialogDescription>Aggiorna i dati e salva.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome *</Label>
              <Input data-testid="expense-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Descrizione</Label>
              <Input data-testid="expense-edit-description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Importo (€) *</Label>
                <Input data-testid="expense-edit-amount" type="number" step="any" min="0" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} className="text-right font-['JetBrains_Mono']" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Data</Label>
                <Input data-testid="expense-edit-date" type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingId(null)}>Annulla</Button>
            <Button data-testid="expense-save-edit-btn" onClick={saveEdit} disabled={savingEdit} className="bg-rose-600 hover:bg-rose-700 text-white">
              {savingEdit ? 'Salvo…' : 'Salva modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <AlertDialogContent data-testid="expense-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Eliminare questa uscita?
            </AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;operazione non è annullabile. Confermi l&apos;eliminazione?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="expense-confirm-delete-btn"
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Elimino…' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExpensesPage;
